<template>
  <span
    :class="['status-badge', `status-badge--${computedVariant}`]"
    :title="tooltip"
  >
    <font-awesome-icon
      v-if="computedIcon"
      :icon="`fa-solid fa-${computedIcon}`"
      class="status-badge__icon"
    />
    <span class="status-badge__label">
      {{ label }}
    </span>
  </span>
</template>

<script>
  import { getStatusDisplay } from '@/constants/status-constants'

  export default {
    name: 'StatusBadge',
    props: {
      // Type of status: 'transaction', 'payment', 'prePayment', or 'custom'
      statusType: {
        type: String,
        default: 'custom'
      },
      // Status code (number) - will auto-lookup display properties
      statusCode: {
        type: Number,
        default: null
      },
      // Manual variant override: 'success', 'warning', 'error', 'info', 'neutral'
      variant: {
        type: String,
        default: null
      },
      // Manual icon override
      icon: {
        type: String,
        default: null
      },
      // Label text to display
      label: {
        type: String,
        required: true
      },
      // Optional tooltip
      tooltip: {
        type: String,
        default: ''
      }
    },
    computed: {
      computedVariant() {
        // If variant is manually set, use it
        if (this.variant) {
          return this.variant
        }

        // If statusType and statusCode are provided, look up the display config
        if (this.statusType !== 'custom' && this.statusCode !== null) {
          const display = getStatusDisplay(this.statusType, this.statusCode)
          return display.variant
        }

        // Default to neutral
        return 'neutral'
      },
      computedIcon() {
        // If icon is manually set, use it
        if (this.icon) {
          return this.icon
        }

        // If statusType and statusCode are provided, look up the display config
        if (this.statusType !== 'custom' && this.statusCode !== null) {
          const display = getStatusDisplay(this.statusType, this.statusCode)
          return display.icon
        }

        // No icon by default
        return null
      }
    }
  }
</script>

<style scoped lang="scss">
  @use '@/main';

  .status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
    line-height: 1.2;
    min-height: 22px;
  }

  .status-badge__icon {
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 12px;
  }

  .status-badge__label {
    line-height: 1;
  }

  // Success variant (green)
  .status-badge--success {
    background: #dcfce7;
    color: #166534;
  }

  // Warning variant (amber)
  .status-badge--warning {
    background: #fef3c7;
    color: #92400e;
  }

  // Error variant (red)
  .status-badge--error {
    background: #fef2f2;
    color: #dc2626;
  }

  // Info variant (blue)
  .status-badge--info {
    background: #e0f2fe;
    color: #0369a1;
  }

  // Neutral variant (gray)
  .status-badge--neutral {
    background: #f3f4f6;
    color: #6b7280;
  }
</style>
